/**
 * useAuth — authentication state + actions, shared via React context.
 *
 * Wrap the app in <AuthProvider> (see index.js); components then call
 * useAuth() to read { user, isAuthenticated } and invoke login/signup/logout.
 *
 * Tokens and the user object are persisted to localStorage so a refresh
 * keeps the session. The API client emits LOGOUT_EVENT when a refresh
 * fails; we listen for it here to drop React state in lockstep.
 */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { post } from '../api/client';
import { TOKEN_KEY, REFRESH_KEY, USER_KEY, LOGOUT_EVENT } from '../config';

const AuthContext = createContext(null);

function readStoredUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(readStoredUser);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const persistSession = useCallback((data) => {
    // Backend returns { user, accessToken, refreshToken }.
    localStorage.setItem(TOKEN_KEY, data.accessToken);
    localStorage.setItem(REFRESH_KEY, data.refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    setToken(data.accessToken);
    setUser(data.user);
  }, []);

  // Merge updated fields into the cached user (e.g. after a profile save) so the
  // whole app — Dashboard, Chef Card, Resources — reflects the change immediately.
  const updateUser = useCallback((partial) => {
    setUser((prev) => {
      const next = { ...(prev || {}), ...partial };
      localStorage.setItem(USER_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const login = useCallback(
    async (email, password) => {
      const data = await post('/api/auth/login', { email, password });
      persistSession(data);
      return data.user;
    },
    [persistSession]
  );

  const signup = useCallback(
    async (email, password, firstName, allergies) => {
      const data = await post('/api/auth/signup', {
        email,
        password,
        first_name: firstName,
        allergies,
      });
      persistSession(data);
      return data.user;
    },
    [persistSession]
  );

  // The API client broadcasts this when refresh fails irrecoverably.
  useEffect(() => {
    const handler = () => logout();
    window.addEventListener(LOGOUT_EVENT, handler);
    return () => window.removeEventListener(LOGOUT_EVENT, handler);
  }, [logout]);

  const value = {
    user,
    isAuthenticated: Boolean(token && user),
    login,
    signup,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default function useAuth() {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth must be used within an <AuthProvider>');
  }
  return ctx;
}
